import * as client from "../../../client";
import { perClient } from "../../../client-features";
import {
  CookingClassEditor,
  Editor,
  MoneyEditor,
  OneLineStringEditor,
  StateEditor,
  YesNoEditor,
} from "./CellEditors";

export type CustomAction =
  | "open_edit_modal_construction"
  //| "open_edit_modal_occupancy"
  | "open_edit_modal_building"
  | "open_street_view_modal";

export type SovColumnProps = {
  getValue: (data: client.RawAddress) => string | number | undefined | null;
  columnName: string;
  width: string;
  field?: keyof client.RawAddress;
  editor?: Editor;
  customAction?: CustomAction;
  bulkEdit?: boolean;
  isIconValue?: boolean;
  showTooltipsWhenNotFlagged?: boolean;
};

const MAP_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => {
    const hasCoords = data.lat && data.long;
    return hasCoords ? "pi pi-map" : null;
  },
  columnName: "Map",
  width: "32px",
  isIconValue: true,
  customAction: "open_street_view_modal",
  field: "lat",
};
const LOC_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.premises_number,
  columnName: "Loc",
  width: "40px",
  field: "premises_number",
  editor: OneLineStringEditor,
};
const BLDG_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.building_number,
  columnName: "Bldg",
  width: "40px",
  field: "building_number",
  editor: OneLineStringEditor,
};
const ADDRESS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.street_address,
  columnName: "Address",
  width: "200px",
  field: "street_address",
  editor: OneLineStringEditor,
};
const CITY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.city,
  columnName: "City",
  width: "150px",
  field: "city",
  editor: OneLineStringEditor,
};
const STATE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.state,
  columnName: "State",
  width: "85px",
  field: "state",
  editor: StateEditor,
};
const ZIP_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.zip,
  columnName: "Zip",
  width: "75px",
  field: "zip",
  editor: OneLineStringEditor,
};
const COUNTRY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.country_code,
  columnName: "Country",
  width: "75px",
  field: "country_code",
  editor: OneLineStringEditor,
};
const COUNTY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.county,
  columnName: "County",
  width: "100px",
  field: "county",
  editor: OneLineStringEditor,
};
const CONSTRUCTION_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    !data.construction_code_type || !data.construction_code
      ? "None"
      : `${data.construction_code_type} ${data.construction_code}${
          data.construction_description
            ? ` (${data.construction_description})`
            : ""
        }`,
  width: "180px",
  columnName: "Construction",
  field: "construction_code",
  customAction: "open_edit_modal_construction",
};
const YEAR_BUILT_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.year_built,
  columnName: "Year Built",
  width: "110px",
  field: "year_built",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const STORIES_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.number_of_stories,
  columnName: "Stories",
  width: "90px",
  field: "number_of_stories",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const SQFT_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.square_footage,
  columnName: "Sqft",
  width: "90px",
  field: "square_footage",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const VALUE_360_COLUMN: SovColumnProps = {
  getValue: () => undefined,
  columnName: "360 Value",
  width: "125px",
};
const BUILDING_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.building_value ? parseFloat(data.building_value) : undefined,
  columnName: "Building Value",
  width: "125px",
  field: "building_value",
  editor: MoneyEditor,
};
const BPP_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.bpp_value ? parseFloat(data.bpp_value) : undefined,
  columnName: "BPP Value",
  width: "125px",
  field: "bpp_value",
  editor: MoneyEditor,
};
const BI_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.business_interruption_value
      ? parseFloat(data.business_interruption_value)
      : undefined,
  columnName: "BI Value",
  width: "125px",
  field: "business_interruption_value",
  editor: MoneyEditor,
};
const TB_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.tenant_betterments_value
      ? parseFloat(data.tenant_betterments_value)
      : undefined,
  columnName: "Tenant Betterments",
  width: "160px",
  field: "tenant_betterments_value",
  editor: MoneyEditor,
};
const GLASS_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.glass_value ? parseFloat(data.glass_value) : undefined,
  columnName: "Glass",
  width: "100px",
  field: "glass_value",
  editor: MoneyEditor,
};
const SIGNS_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.signs_value ? parseFloat(data.signs_value) : undefined,
  columnName: "Signs",
  width: "100px",
  field: "signs_value",
  editor: MoneyEditor,
};
const TIV_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.tiv,
  columnName: "TIV",
  width: "125px",
  field: "tiv",
  editor: MoneyEditor,
};
const AOP_DED_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.aop_deductible,
  columnName: "AOP Ded",
  width: "125px",
  field: "aop_deductible",
  editor: MoneyEditor,
};
const SPRINKLER_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.sprinkler,
  columnName: "Sprinkler",
  width: "110px",
  field: "sprinkler",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
};
const FIRE_ALARM_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.fire_alarm_type,
  columnName: "Fire Alarm",
  width: "110px",
  field: "fire_alarm_type",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
};
const BURGLAR_ALARM_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.burglar_alarm_type,
  columnName: "Burglar Alarm",
  width: "130px",
  field: "burglar_alarm_type",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
};
const ROOFING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.roofing_year,
  columnName: "Roofing",
  width: "100px",
  field: "roofing_year",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const HEATING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.heating_year,
  columnName: "Heating",
  width: "100px",
  field: "heating_year",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const WIRING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.wiring_year,
  columnName: "Wiring",
  width: "100px",
  field: "wiring_year",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const PLUMBING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.plumbing_year,
  columnName: "Plumbing",
  width: "100px",
  field: "plumbing_year",
  editor: OneLineStringEditor,
  bulkEdit: true,
};
const BUSINESS_INFORMATION_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.building_description ?? "None",
  columnName: "Business Information",
  width: "240px",
  field: "building_description",
  customAction: "open_edit_modal_building",
};
const COOKING_CLASS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.cooking_class ?? "None",
  columnName: "Cooking",
  width: "140px",
  field: "cooking_class",
  editor: CookingClassEditor,
};
const NUMBER_OF_UNITS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.number_of_units,
  columnName: "# of Units",
  width: "80px",
  field: "number_of_units",
  editor: OneLineStringEditor,
};
const NUMBER_OF_BUILDINGS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.number_of_buildings,
  columnName: "# of Buildings",
  width: "110px",
  field: "number_of_buildings",
  editor: OneLineStringEditor,
};
const NUMBER_OF_CANOPY_BUILDINGS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.num_canopy_buildings,
  columnName: "# of Canopy Buildings",
  width: "170px",
  field: "num_canopy_buildings",
  editor: OneLineStringEditor,
};
const CLIMATE_CONTROLLED_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.climate_controlled,
  columnName: "Climate Controlled",
  width: "150px",
  field: "climate_controlled",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const IS_FENCED_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.is_fenced,
  columnName: "Fenced",
  width: "80px",
  field: "is_fenced",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const HAS_LIGHTING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.has_lighting,
  columnName: "Lighting",
  width: "90px",
  field: "has_lighting",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const CONTROLLED_GATE_ACCESS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.controlled_gate_access,
  columnName: "Controlled Gate Access",
  width: "180px",
  field: "controlled_gate_access",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const SURVEILLANCE_CAMERAS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.has_surveillance_cameras,
  columnName: "Surveillance Cameras",
  width: "170px",
  field: "has_surveillance_cameras",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const OUTDOOR_SPACES_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.num_outdoor_spaces,
  columnName: "Outdoor Spaces",
  width: "140px",
  field: "num_outdoor_spaces",
  editor: OneLineStringEditor,
  showTooltipsWhenNotFlagged: true,
};
const VACANT_ACRES_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.num_vacant_acres,
  columnName: "Vacant Acres",
  width: "130px",
  field: "num_vacant_acres",
  editor: OneLineStringEditor,
  showTooltipsWhenNotFlagged: true,
};
const CENTRAL_STATION_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.has_central_station_alarm,
  columnName: "CS Alarm",
  width: "100px",
  field: "has_central_station_alarm",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const HAS_CANOPY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.has_canopy_roof,
  columnName: "Canopy",
  width: "80px",
  field: "has_canopy_roof",
  editor: YesNoEditor,
};
const PROT_CLASS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.protection_class,
  columnName: "Prot Class",
  width: "100px",
  field: "protection_class",
  editor: OneLineStringEditor,
};
const TERRITORY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.territory,
  columnName: "Territory",
  width: "100px",
  field: "territory",
  editor: OneLineStringEditor,
};
const SCS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.scs_score,
  columnName: "SCS",
  width: "60px",
  field: "scs_score",
  editor: OneLineStringEditor,
};
const WILDFIRE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.wildfire_score,
  columnName: "Wildfire",
  width: "80px",
  field: "wildfire_score",
  editor: OneLineStringEditor,
};
const WIND_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.wind_score,
  columnName: "Wind",
  width: "60px",
  field: "wind_score",
  editor: OneLineStringEditor,
};
const HAIL_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.hail_score,
  columnName: "Hail",
  width: "60px",
  field: "hail_score",
  editor: OneLineStringEditor,
};
const TORNADO_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.tornado_score,
  columnName: "Tornado",
  width: "80px",
  field: "tornado_score",
  editor: OneLineStringEditor,
};
const HURRICANE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.tornado_score,
  columnName: "Hurricane",
  width: "100px",
  field: "hurricane_score",
  editor: OneLineStringEditor,
};
const OCCUPANCY_PERCENTAGE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.occupancy_percentage,
  columnName: "Occ %",
  width: "80px",
  field: "occupancy_percentage",
  editor: OneLineStringEditor,
  showTooltipsWhenNotFlagged: true,
};
const RESIDENT_MANAGER_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.resident_manager,
  columnName: "Resident Manager",
  width: "160px",
  field: "resident_manager",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const OFFSITE_RENTAL_OFFICE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.offsite_rental_office,
  columnName: "Offsite Rental Office",
  width: "160px",
  field: "offsite_rental_office",
  editor: YesNoEditor,
  showTooltipsWhenNotFlagged: true,
};
const MOBILE_STORAGE_UNIT_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.num_mobile_storage_units,
  columnName: "Mobile Storage Units",
  width: "170px",
  field: "num_mobile_storage_units",
  editor: OneLineStringEditor,
  showTooltipsWhenNotFlagged: true,
};
const CONVERTED_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.converted,
  columnName: "Converted",
  showTooltipsWhenNotFlagged: true,
  width: "100px",
  field: "converted",
  editor: OneLineStringEditor,
};
const NON_STORAGE_TENANTS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.non_storage_tenants,
  columnName: "Non Storage Tenants",
  width: "170px",
  field: "non_storage_tenants",
  editor: OneLineStringEditor,
  showTooltipsWhenNotFlagged: true,
};

const BASE_COLUMNS_1 = [MAP_COLUMN, LOC_COLUMN, BLDG_COLUMN];

const LOCATION_COLUMNS = ({
  county,
  country,
}: {
  county: boolean;
  country: boolean;
}) => [
  ADDRESS_COLUMN,
  CITY_COLUMN,
  STATE_COLUMN,
  ZIP_COLUMN,
  ...(county ? [COUNTY_COLUMN] : []),
  ...(country ? [COUNTRY_COLUMN] : []),
];

const BASE_COLUMNS_2 = [
  CONSTRUCTION_COLUMN,
  YEAR_BUILT_COLUMN,
  STORIES_COLUMN,
  SQFT_COLUMN,
];

const BASE_COLUMNS_3 = [
  BUILDING_VALUE_COLUMN,
  BPP_VALUE_COLUMN,
  BI_VALUE_COLUMN,
  TB_VALUE_COLUMN,
  GLASS_VALUE_COLUMN,
  SIGNS_VALUE_COLUMN,
  TIV_COLUMN,
  AOP_DED_COLUMN,
  SPRINKLER_COLUMN,
];

const BASE_COLUMNS_4 = [
  ROOFING_COLUMN,
  HEATING_COLUMN,
  WIRING_COLUMN,
  PLUMBING_COLUMN,
];

const UIP_ADDITIONAL_COLUMNS = [
  NUMBER_OF_UNITS_COLUMN,
  NUMBER_OF_BUILDINGS_COLUMN,
  NUMBER_OF_CANOPY_BUILDINGS_COLUMN,
  CLIMATE_CONTROLLED_COLUMN,
  IS_FENCED_COLUMN,
  HAS_LIGHTING_COLUMN,
  CONTROLLED_GATE_ACCESS_COLUMN,
  SURVEILLANCE_CAMERAS_COLUMN,
  OUTDOOR_SPACES_COLUMN,
  VACANT_ACRES_COLUMN,
  CENTRAL_STATION_COLUMN,
  HAS_CANOPY_COLUMN,
  OCCUPANCY_PERCENTAGE_COLUMN,
  RESIDENT_MANAGER_COLUMN,
  OFFSITE_RENTAL_OFFICE_COLUMN,
  MOBILE_STORAGE_UNIT_COLUMN,
  CONVERTED_COLUMN,
  NON_STORAGE_TENANTS_COLUMN,
  PROT_CLASS_COLUMN,
  TERRITORY_COLUMN,
  SCS_COLUMN,
  WILDFIRE_COLUMN,
  WIND_COLUMN,
  HAIL_COLUMN,
  TORNADO_COLUMN,
  HURRICANE_COLUMN,
];

const BLUESKY_EXTRAS = [BUSINESS_INFORMATION_COLUMN, COOKING_CLASS_COLUMN];

export const ADDRESS_COLUMNS = perClient<SovColumnProps[]>({
  bluesky: [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS({ county: false, country: true }),
    ...BASE_COLUMNS_2,
    VALUE_360_COLUMN,
    ...BASE_COLUMNS_3,
    FIRE_ALARM_COLUMN,
    BURGLAR_ALARM_COLUMN,
    ...BASE_COLUMNS_4,
    ...BLUESKY_EXTRAS,
  ],
  pantheon: [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS({ county: true, country: true }),
    ...BASE_COLUMNS_2,
    VALUE_360_COLUMN,
    ...BASE_COLUMNS_3,
    FIRE_ALARM_COLUMN,
    BURGLAR_ALARM_COLUMN,
    ...BASE_COLUMNS_4,
    ...BLUESKY_EXTRAS,
  ],
  uip: [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS({ county: true, country: false }),
    ...BASE_COLUMNS_2,
    ...BASE_COLUMNS_3,
    ...BASE_COLUMNS_4,
    ...UIP_ADDITIONAL_COLUMNS,
  ],
});
